// Pure derivation logic for the Assessment QC Review worklist (AssessmentQCReviewPage).
// No JSX, no side effects — this is the entire ordering concept for the page, so it is
// unit-tested independently of any rendering.

import { QCReviewItem, QCReviewSubControl } from '../../../interfaces/qcReviewData.interface';
import {
  QCErrorClass,
  QCReview,
  QCVerdict,
  VerificationStatus,
  VERIFICATION_STATUSES,
  QC_FLAGGED_VERDICTS,
} from '../../../interfaces/qcReview.interface';
// Aggregate "Fully/Not/Partially Implemented" etc. options mixed into Question.answers
// alongside the real sub-control line-items (CONTEXT.md: "Line-items vs aggregate call").
// Sub-control review targets exclude these.
export const PLACEHOLDER_ANSWER_TEXTS = ['Fully Implemented', 'Not Implemented', 'Not Applicable'];

export function isRealSubControl(subControl: QCReviewSubControl): boolean {
  return !PLACEHOLDER_ANSWER_TEXTS.includes(subControl.text);
}

export function getSPDisposition(item: QCReviewItem, subControlId: number): string | undefined {
  const sp = item.spDisposition;
  if (!sp) return undefined;
  if (sp.answer.includes(subControlId)) return 'implemented';
  if (sp.compensatingControlAnswers.includes(subControlId)) return 'compensating_control';
  if (sp.nonApplicableAnswers.includes(subControlId)) return 'not_applicable';
  if (sp.nonImplementedAnswers.includes(subControlId)) return 'not_implemented';
  return undefined;
}

// A roll-up verdict targets the Question's AssessorAnswer directly rather than a sub-control
// Answer, so it carries no answer_id. Check target_grain first for forward-compatibility with
// the pending backend change; fall back to the null/undefined answer_id tell.
export function isRollupReview(qcReview: QCReview): boolean {
  if (qcReview.target_grain) return qcReview.target_grain === 'rollup';
  return qcReview.answer_id === null || qcReview.answer_id === undefined;
}

// Mirrors USER_TYPES.ASSESSOR (services/user.ts) — inlined so this pure module doesn't pull
// in that service's axios/Amplify import chain.
const ASSESSOR_USER_TYPE = 'assessor';

export type QCAttention = 'needs-me' | 'awaiting-assessor' | 'done';

export interface QCAttentionRow {
  id: number;
  attention: QCAttention;
  worstVerdict: QCVerdict;
  flaggedCount: number;
  subControlCount: number;
  errorClasses: QCErrorClass[];
  minConfidence: number | null;
  suspectedInjection: boolean;
  lastCommentAt: string | null;
  lastCommentAuthorType: string | null;
  commentCount: number;
  spDivergence: boolean;
  verificationStatus: VerificationStatus;
}

// Lower = more severe / more deserving of attention.
const VERDICT_SEVERITY: Record<QCVerdict, number> = {
  disagree: 0,
  uncertain: 1,
  not_evaluated: 2,
  agree: 3,
};

export const ATTENTION_ORDER: Record<QCAttention, number> = {
  'needs-me': 0,
  'awaiting-assessor': 1,
  done: 2,
};

export function compareVerdictSeverity(a: QCVerdict, b: QCVerdict): number {
  return VERDICT_SEVERITY[a] - VERDICT_SEVERITY[b];
}

export function compareAttention(a: QCAttention, b: QCAttention): number {
  return ATTENTION_ORDER[a] - ATTENTION_ORDER[b];
}

// Ascending; rows with no confidence value (nothing evaluated) sort after every scored row.
export function compareMinConfidence(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

function worstOf(reviews: QCReview[]): QCVerdict {
  if (reviews.length === 0) return 'not_evaluated';
  return reviews.reduce<QCVerdict>(
    (worst, review) => (compareVerdictSeverity(review.verdict, worst) < 0 ? review.verdict : worst),
    reviews[0].verdict,
  );
}

function lastComment(item: QCReviewItem) {
  if (item.comments.length === 0) return undefined;
  return item.comments.reduce((latest, comment) =>
    new Date(comment.createdAt).getTime() > new Date(latest.createdAt).getTime() ? comment : latest,
  );
}

function computeAttention(
  flaggedCount: number,
  verificationStatus: VerificationStatus,
  lastCommentAuthorType: string | null,
): QCAttention {
  const hasUnresolvedFlag = flaggedCount > 0 && verificationStatus === VERIFICATION_STATUSES.NOT_STARTED;
  if (hasUnresolvedFlag) return 'needs-me';
  if (lastCommentAuthorType === ASSESSOR_USER_TYPE) return 'awaiting-assessor';
  return 'done';
}

// verificationStatus is read directly off the item - it's server state (AssessorAnswer.
// verification_status), not a parameter the caller supplies locally.
export function deriveRow(item: QCReviewItem, qcReviews: QCReview[]): QCAttentionRow {
  const verificationStatus: VerificationStatus = item.verificationStatus ?? VERIFICATION_STATUSES.NOT_STARTED;

  // Defensive re-scope: correctness of this module must not depend on callers pre-filtering
  // qcReviews to this item, and rollup rows (no per-sub-control answer_id) must not be
  // dropped by that scoping.
  const relevantReviews = qcReviews.filter(qc => qc.is_current && qc.assessor_answer_id === item.id);

  const flaggedCount = relevantReviews.filter(qc => QC_FLAGGED_VERDICTS.includes(qc.verdict)).length;

  const errorClasses = Array.from(
    new Set(
      relevantReviews
        .map(qc => qc.error_class)
        .filter((errorClass): errorClass is QCErrorClass => !!errorClass && errorClass !== 'none'),
    ),
  );

  const confidences = relevantReviews
    .map(qc => qc.confidence)
    .filter((confidence): confidence is number => confidence !== null && confidence !== undefined);

  const subControlCount = item.question.subControls.filter(isRealSubControl).length;

  const spDivergence = item.question.subControls.filter(isRealSubControl).some(subControl => {
    const assessorQuestionAnswer = item.assessorQuestionAnswers.find(aqa => aqa.answerId === subControl.id);
    const assessorDisposition = assessorQuestionAnswer?.compensatingOrNotApplicable || undefined;
    const spDisposition = getSPDisposition(item, subControl.id);
    return !!spDisposition && !!assessorDisposition && spDisposition !== assessorDisposition;
  });

  const latestComment = lastComment(item);

  const lastCommentAt = latestComment?.createdAt ?? null;
  const lastCommentAuthorType = latestComment?.user.type ?? null;

  return {
    id: item.id,
    attention: computeAttention(flaggedCount, verificationStatus, lastCommentAuthorType),
    worstVerdict: worstOf(relevantReviews),
    flaggedCount,
    subControlCount,
    errorClasses,
    minConfidence: confidences.length > 0 ? Math.min(...confidences) : null,
    suspectedInjection: relevantReviews.some(qc => qc.suspected_injection),
    lastCommentAt,
    lastCommentAuthorType,
    commentCount: item.comments.length,
    spDivergence,
    verificationStatus,
  };
}
